// src/components/Budget/BudgetForm.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  Stack,
  Paper,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useAccount } from '../../contexts/AccountContext';

export const BudgetForm = ({ category, onSave, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [errorMessage, setErrorMessage] = useState('');
  const { accounts } = useAccount();
  const [fundingAccountId, setFundingAccountId] = useState(category?.fundingAccountId || '');
  
  // Log props on component mount to debug
  useEffect(() => {
    console.log('BudgetForm mounted with props:', { 
      hasCategory: !!category, 
      hasOnSave: typeof onSave === 'function',
      hasOnClose: typeof onClose === 'function'
    });
  }, [category, onSave, onClose]);
  
  const [formData, setFormData] = useState({
    name: category?.name || '',
    budget: category?.budget || '',
    color: category?.color || '#2196f3',
    description: category?.description || '',
    targetPercentage: category?.targetPercentage || '',
    icon: category?.icon || '💰',
  });

  // Update form when category prop changes
  useEffect(() => {
    console.log('📝 BudgetForm: category prop changed:', {
      hasCategory: !!category,
      categoryName: category?.name,
      categoryBudget: category?.budget,
      categoryMonthlyBudgets: category?.monthlyBudgets
    });
    
    if (category) {
      const newFormData = {
        name: category.name || '',
        budget: category.budget || '',
        color: category.color || '#2196f3',
        description: category.description || '',
        targetPercentage: category.targetPercentage || '',
        icon: category.icon || '💰',
      };
      
      console.log('📝 BudgetForm: Setting form data:', newFormData);
      setFormData(newFormData);
      setFundingAccountId(category.fundingAccountId || '');
    }
  }, [category]);

  const [errors, setErrors] = useState({});
  const commonIcons = ['🏠', '🍽️', '🚗', '💰', '🎮', '🏥', '✈️', '📚', '🎭', '👕', '💳', '🛒', '🐈', '🏋🏽‍♂️'];
  
  // Predefined colors for quick selection
  const predefinedColors = [
    '#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6', 
    '#1abc9c', '#d35400', '#34495e', '#27ae60', '#e84393'
  ];

  const validateForm = () => {
    console.log('🔍 BudgetForm: Validating form with data:', formData);
    
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
      console.log('❌ Validation: Name is required');
    }
    
    const budgetValue = parseFloat(formData.budget);
    console.log('💰 Validation: Budget value parsed as:', budgetValue, 'from:', formData.budget);
    
    if (!formData.budget || isNaN(budgetValue) || budgetValue <= 0) {
      newErrors.budget = 'Budget must be greater than 0';
      console.log('❌ Validation: Budget must be greater than 0');
    }
    
    if (formData.targetPercentage) {
      const percentage = parseFloat(formData.targetPercentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        newErrors.targetPercentage = 'Percentage must be between 0 and 100';
        console.log('❌ Validation: Target percentage invalid');
      }
    }
    
    console.log('✅ Validation result:', Object.keys(newErrors).length === 0 ? 'PASSED' : 'FAILED', newErrors);
    return newErrors;
  };

  // Internal save handler with fallback
  const handleSaveInternal = (formattedData) => {
    console.log('💾 BudgetForm: handleSaveInternal called with:', formattedData);
    console.log('🔗 onSave prop type:', typeof onSave);
    
    if (typeof onSave === 'function') {
      // Use the prop if it's a function
      console.log('✅ BudgetForm: Calling onSave prop...');
      onSave(formattedData);
    } else {
      console.warn('❌ BudgetForm: No onSave handler provided, using fallback local storage save');
      
      // Fallback: Save to localStorage
      try {
        const categories = JSON.parse(localStorage.getItem('budget-categories') || '[]');
        
        // Update existing or add new
        const existingIndex = categories.findIndex(c => c.id === formattedData.id);
        if (existingIndex >= 0) {
          categories[existingIndex] = formattedData;
        } else {
          categories.push(formattedData);
        }
        
        localStorage.setItem('budget-categories', JSON.stringify(categories));
        
        // Show success message
        setErrorMessage('Category saved to local storage');
        
        // Try to close the dialog if onClose exists
        if (typeof onClose === 'function') {
          onClose();
        }
      } catch (err) {
        console.error('Error saving to localStorage:', err);
        setErrorMessage('Failed to save category');
      }
    }
  };

  // Internal close handler with fallback
  const handleCloseInternal = () => {
    if (typeof onClose === 'function') {
      onClose();
    } else {
      console.warn('No onClose handler provided');
      // No good fallback for closing a dialog
      setErrorMessage('Unable to close dialog. Try clicking outside or refreshing the page.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('🚀 BudgetForm: handleSubmit called');
    console.log('📊 Current form data:', formData);
    
    const newErrors = validateForm();
    console.log('✅ Validation errors:', newErrors);

    if (Object.keys(newErrors).length === 0) {
      const formattedData = {
        ...formData,
        id: category?.id || Date.now(),
        budget: parseFloat(formData.budget),
        targetPercentage: formData.targetPercentage ? parseFloat(formData.targetPercentage) : null,
        name: formData.name.trim(),
        color: formData.color || '#2196f3',
        icon: formData.icon || '💰',
        fundingAccountId: fundingAccountId || null,
      };

      console.log('💾 BudgetForm: Submitting formatted data:', formattedData);
      handleSaveInternal(formattedData);
    } else {
      console.error('❌ BudgetForm: Form validation failed:', newErrors);
      setErrors(newErrors);
    }
  };

  // Space placeholder to prevent layout shift
  const emptyHelperText = ' ';

  return (
    <>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <DialogContent sx={{ 
          maxHeight: isMobile ? 'calc(100vh - 120px)' : '500px', 
          overflow: 'auto',
          px: { xs: 2, sm: 3 }
        }}>
          <Stack spacing={3} sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name || emptyHelperText}
              margin="normal"
              size={isMobile ? "small" : "medium"}
              autoFocus={!isMobile}
            />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Category Icon
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 2,
                  justifyContent: 'flex-start',
                }}
              >
                {commonIcons.map((icon) => (
                  <Paper
                    key={icon}
                    elevation={formData.icon === icon ? 2 : 0}
                    onClick={() => setFormData({ ...formData, icon })}
                    sx={{
                      width: isMobile ? 40 : 46,
                      height: isMobile ? 40 : 46,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      borderRadius: 2,
                      fontSize: isMobile ? 20 : 24,
                      border: '1px solid',
                      borderColor: formData.icon === icon 
                        ? 'primary.main' 
                        : 'divider',
                      bgcolor: formData.icon === icon 
                        ? alpha(theme.palette.primary.main, 0.1)
                        : 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    {icon}
                  </Paper>
                ))}
              </Box>
              <TextField
                fullWidth
                placeholder="Or type custom emoji/icon"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                size={isMobile ? "small" : "medium"}
              />
            </Box>

            <TextField
              fullWidth
              type="number"
              label="Monthly Budget"
              value={formData.budget}
              onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
              error={!!errors.budget}
              helperText={errors.budget || 'Set budget for the current month'}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
                inputProps: { min: 0, step: '0.01' },
              }}
              size={isMobile ? "small" : "medium"}
            />

            <TextField
              fullWidth
              type="number"
              label="Target Percentage of Total Budget"
              value={formData.targetPercentage}
              onChange={(e) => setFormData({ ...formData, targetPercentage: e.target.value })}
              error={!!errors.targetPercentage}
              helperText={errors.targetPercentage || 'Enter percentage (0-100)'}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: 0, max: 100, step: '0.1' },
              }}
              size={isMobile ? "small" : "medium"}
            />

            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add notes or spending guidelines"
              size={isMobile ? "small" : "medium"}
            />

            {/* Funding Account picker */}
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel>Funded from account</InputLabel>
              <Select
                value={fundingAccountId}
                label="Funded from account"
                onChange={(e) => setFundingAccountId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Not linked to an account</em>
                </MenuItem>
                {accounts.map((acc) => (
                  <MenuItem key={acc._id} value={acc._id}>
                    {acc.icon || '🏦'} {acc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Fixed Color Selection */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Category Color
              </Typography>
              
              {/* Color preview and picker */}
              <Stack 
                direction="row" 
                spacing={2} 
                alignItems="center" 
                sx={{ mb: 2 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `2px solid ${theme.palette.divider}`,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: theme.shadows[2]
                    }
                  }}
                >
                  {/* Color preview */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: formData.color,
                    }}
                  />
                  
                  {/* Actual color input - using native HTML element */}
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer',
                    }}
                  />
                </Box>
                
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight="medium">
                    {formData.color}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click the square to select a custom color
                  </Typography>
                </Stack>
              </Stack>
              
              {/* Quick color options */}
              <Typography variant="subtitle2" gutterBottom>
                Quick Colors
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {predefinedColors.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: color,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: formData.color === color ? '2px solid #fff' : '2px solid transparent',
                      outline: formData.color === color ? `2px solid ${color}` : 'none',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.15)',
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>

        {/* Enhanced DialogActions with better mobile spacing and order */}
        <DialogActions 
          sx={{ 
            display: 'flex',
            flexDirection: isMobile ? 'column-reverse' : 'row',  // Reversed column on mobile
            justifyContent: isMobile ? 'stretch' : 'flex-end', 
            alignItems: 'stretch',
            gap: isMobile ? 2 : 1.5,
            px: 3,
            py: 3,
            mt: 2, 
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
          }}
        >
          <Button 
            onClick={handleCloseInternal} 
            variant="outlined" 
            fullWidth={isMobile}
            color="inherit"
            sx={{ 
              borderRadius: 2,
              py: isMobile ? 1.2 : 1,
              fontSize: isMobile ? '0.95rem' : '0.875rem',
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.text.secondary,
                bgcolor: alpha(theme.palette.divider, 0.1)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            fullWidth={isMobile}
            sx={{ 
              borderRadius: 2, 
              py: isMobile ? 1.2 : 1,
              fontSize: isMobile ? '0.95rem' : '0.875rem',
              fontWeight: 600,
              boxShadow: isMobile ? 3 : 1,
              background: isMobile 
                ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                : theme.palette.primary.main,
              '&:hover': {
                boxShadow: 4,
                background: isMobile 
                  ? `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                  : theme.palette.primary.dark
              }
            }}
          >
            {category ? 'Update' : 'Create'} Category
          </Button>
        </DialogActions>
      </form>

      {/* Error/Success Notification */}
      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setErrorMessage('')} 
          severity={errorMessage.includes('Failed') || errorMessage.includes('Unable') ? 'error' : 'success'} 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

BudgetForm.propTypes = {
  category: PropTypes.object,
  onSave: PropTypes.func,  // Made optional
  onClose: PropTypes.func, // Made optional
};

// Default props as a fallback
BudgetForm.defaultProps = {
  onSave: null, // Will trigger the internal fallback
  onClose: null, // Will trigger the internal fallback
};

export default BudgetForm;